import { useContext, useEffect, useState } from "react";

import { DetectionTool, loadDetectionQuota } from "../api";
import { UserContext } from "features/account";

/**
 * Accès aux outils de vérification — règle unique du produit :
 *   3 vérifications gratuites par mois ET PAR OUTIL, puis débit du solde,
 *   et si le solde est vide, on propose l'achat.
 *
 * Ce hook est la SEULE source de cette règle côté web. Les écrans ne doivent
 * plus tester `credits > 0` seuls : un utilisateur à 0 crédit peut très bien
 * avoir ses gratuites du mois intactes.
 *
 * Tolérant par choix : tant que le quota n'est pas connu, ou si l'appel échoue,
 * `canUse` retombe sur l'ancien critère (le solde) — jamais plus restrictif
 * qu'avant.
 */
export function useVerificationAccess() {
    const { user } = useContext(UserContext);
    const userType = user?.type;
    const credits = user?.credits ?? 0;

    const [quota, setQuota] = useState<any>(null);
    const [loaded, setLoaded] = useState<boolean>(false);

    useEffect(() => {
        let cancelled = false;
        loadDetectionQuota(userType).then((q: any) => {
            if (cancelled) return;
            setQuota(q);
            setLoaded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [userType]);

    /** Gratuites restantes ce mois pour un outil (0 si quota inconnu). */
    const freeRemaining = (tool: string): number =>
        quota ? quota.freeRemaining(tool) : 0;

    /** Au moins un outil de vérification est utilisable (gratuit ou payant). */
    const canUse =
        credits > 0 ||
        (quota
            ? quota.credits > 0 ||
              freeRemaining(DetectionTool.documentAdd) > 0 ||
              freeRemaining(DetectionTool.documentVerify) > 0 ||
              freeRemaining(DetectionTool.identity) > 0
            : false);

    return { canUse, freeRemaining, credits, quota, loading: !loaded };
}

export default useVerificationAccess;
